import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import React, { useRef, useState } from "react";

import type { DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { zinc } from "@/ui/tokens";

/**
 * The view's name beside the page title. With more than one view in the cascade it is a dropdown that switches
 * between them; with one it is plain text.
 *
 * @param onOpen - Runs as the dropdown opens, before any choice is made.
 */
export default React.memo(function DispatchViewPicker({
  view,
  views,
  onOpen,
  onSelectView,
}: {
  view: DispatchViewDefinition;
  views: readonly DispatchViewDefinition[];
  onOpen: () => void;
  onSelectView: (viewId: string) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (views.length <= 1) {
    return (
      <Typography component="span" sx={{ color: zinc[400], fontSize: "0.9375rem" }}>
        {view.label}
      </Typography>
    );
  }
  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-sb={TestIds.dispatch.viewSwitcher}
        aria-label="View"
        aria-haspopup="menu"
        onClick={() => {
          onOpen();
          setOpen(true);
        }}
        sx={{
          height: 28,
          pl: 1,
          pr: 0.5,
          gap: 0.25,
          borderRadius: 1,
          color: zinc[200],
          fontSize: "0.9375rem",
          "&:hover": { bgcolor: zinc[800], color: "white" },
        }}
      >
        {view.label}
        <ExpandMoreIcon sx={{ fontSize: 18, color: zinc[400] }} />
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { bgcolor: zinc[800], color: "white", minWidth: 200 } }, list: { dense: true } }}
      >
        {views.map((candidate) => (
          <MenuItem
            key={candidate.id}
            selected={candidate.id === view.id}
            onClick={() => {
              onSelectView(candidate.id);
              setOpen(false);
            }}
            sx={{ fontSize: "0.8125rem" }}
          >
            {candidate.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});
